import { CompositeScoreWeights, CompositeScoreStatistics } from '../types';
import { NormalizedResults } from './normalization';
import { calculateStatistics } from './statistics';

/**
 * Default composite score weights
 */
export const DEFAULT_COMPOSITE_WEIGHTS: CompositeScoreWeights = {
  fitness: 0.5,
  energy: 0.3,
  cost: 0.2,
};

/**
 * Validates that weights sum to 1.0 (within tolerance)
 */
export function validateWeights(weights: CompositeScoreWeights, tolerance: number = 0.01): boolean {
  const sum = weights.fitness + weights.energy + weights.cost;
  return Math.abs(sum - 1.0) < tolerance;
}

/**
 * Normalizes weights to sum to 1.0
 */
export function normalizeWeights(weights: CompositeScoreWeights): CompositeScoreWeights {
  const sum = weights.fitness + weights.energy + weights.cost;
  if (sum === 0) return DEFAULT_COMPOSITE_WEIGHTS;
  
  return {
    fitness: weights.fitness / sum,
    energy: weights.energy / sum,
    cost: weights.cost / sum,
  };
}

/**
 * Calculates composite score for a single run
 * score = w1 * fitness_norm + w2 * (1 - energy_norm) + w3 * (1 - cost_norm)
 * Note: energy_norm and cost_norm are already inverted in normalization
 */
export function calculateCompositeScore(
  fitnessNorm: number,
  energyNorm: number,  // Already inverted (1 - normalized)
  costNorm: number,    // Already inverted (1 - normalized)
  weights: CompositeScoreWeights = DEFAULT_COMPOSITE_WEIGHTS
): number {
  return (
    weights.fitness * fitnessNorm +
    weights.energy * energyNorm +
    weights.cost * costNorm
  );
}

/**
 * Calculates composite scores for all runs and returns statistics per algorithm
 */
export interface CompositeScoreResults {
  perAlgorithm: {
    [algorithmKey: string]: CompositeScoreStatistics;
  };
  perRun: Array<{
    algorithmKey: string;
    runId: number;
    score: number;
  }>;
}

export function calculateCompositeScoreStatistics(
  normalizedResults: NormalizedResults,
  weights: CompositeScoreWeights = DEFAULT_COMPOSITE_WEIGHTS
): CompositeScoreResults {
  // Normalize weights to ensure they sum to 1.0
  const normalizedWeights = normalizeWeights(weights);

  // Calculate composite score for each run
  const perRun: CompositeScoreResults['perRun'] = normalizedResults.normalized.map((norm) => ({
    algorithmKey: norm.algorithmKey,
    runId: norm.runId,
    score: calculateCompositeScore(
      norm.fitnessNorm,
      norm.energyNorm,
      norm.costNorm,
      normalizedWeights
    ),
  }));

  // Group by algorithm and calculate statistics
  const algorithmScores: { [key: string]: number[] } = {};
  
  perRun.forEach(({ algorithmKey, score }) => {
    if (!algorithmScores[algorithmKey]) {
      algorithmScores[algorithmKey] = [];
    }
    algorithmScores[algorithmKey].push(score);
  });

  // Calculate statistics per algorithm
  const perAlgorithm: CompositeScoreResults['perAlgorithm'] = {};
  
  Object.keys(algorithmScores).forEach((algorithmKey) => {
    const scores = algorithmScores[algorithmKey];
    const stats = calculateStatistics(scores);
    
    perAlgorithm[algorithmKey] = {
      median: stats.median,
      mean: stats.mean,
      stdDev: stats.stdDev,
    };
  });

  return {
    perAlgorithm,
    perRun,
  };
}

